import { ICustomerPricing } from '@/supabase/Customer/customer.interface'
import { IOrderCalculation } from '@/supabase/Orderbook/orderbook.interface'

export interface IOrderAmounts {
  largeIceCubeAmount?: number
  smallIceCubeAmount?: number
  crushedIceAmount?: number
  coarselyCrushedIceAmount?: number
}

export interface IOrderPricing {
  largeIceCubePriceTHB: number
  smallIceCubePriceTHB: number
  crushedIcePriceTHB: number
}

/**
 * Calculate total order value using customer pricing
 * @param amounts - Ice cube amounts for the order
 * @param pricing - Customer pricing information
 * @returns Detailed calculation breakdown and total amount
 */
export const calculateOrderTotal = (
  amounts: IOrderAmounts,
  pricing: IOrderPricing
): IOrderCalculation => {
  const largeIceCubeTotal =
    (amounts.largeIceCubeAmount || 0) * pricing.largeIceCubePriceTHB
  const smallIceCubeTotal =
    (amounts.smallIceCubeAmount || 0) * pricing.smallIceCubePriceTHB
  const crushedIceTotal =
    (amounts.crushedIceAmount || 0) * pricing.crushedIcePriceTHB
  const coarselyCrushedIceTotal =
    (amounts.coarselyCrushedIceAmount || 0) * pricing.crushedIcePriceTHB

  const totalAmount =
    largeIceCubeTotal +
    smallIceCubeTotal +
    crushedIceTotal +
    coarselyCrushedIceTotal

  return {
    largeIceCubeTotal,
    smallIceCubeTotal,
    crushedIceTotal,
    coarselyCrushedIceTotal,
    totalAmount,
  }
}

/**
 * Calculate outstanding balance after payment
 * @param totalAmount - Total order amount
 * @param amountPaid - Amount already paid
 * @returns Outstanding balance (0 if fully paid)
 */
export const calculateOutstandingBalance = (
  totalAmount: number,
  amountPaid: number
): number => {
  const balance = totalAmount - amountPaid
  return Math.max(0, balance) // Ensure balance is never negative
}

/**
 * Validate calculation accuracy by comparing calculated total with stored total
 * @param calculatedTotal - Calculated total amount
 * @param storedTotal - Stored total amount in database
 * @param tolerance - Acceptable difference (default: 0.01 THB)
 * @returns True if totals match within tolerance
 */
export const validateCalculationAccuracy = (
  calculatedTotal: number,
  storedTotal: number,
  tolerance = 0.01
): boolean => {
  return Math.abs(calculatedTotal - storedTotal) <= tolerance
}

/**
 * Get pricing from CustomerPricing object
 * @param customerPricing - Customer pricing record
 * @returns Pricing object for calculations
 */
export const extractPricingFromCustomerPricing = (
  customerPricing: ICustomerPricing
): IOrderPricing => {
  return {
    largeIceCubePriceTHB: customerPricing.large_ice_cube_price_thb,
    smallIceCubePriceTHB: customerPricing.small_ice_cube_price_thb,
    crushedIcePriceTHB: customerPricing.crushed_ice_price_thb,
  }
}

/**
 * Format currency amount for display
 * @param amount - Amount in THB
 * @returns Formatted string with currency symbol
 */
export const formatCurrency = (amount: number): string => {
  return `฿${amount.toFixed(2)}`
}

/**
 * Calculate payment completion percentage
 * @param totalAmount - Total order amount
 * @param amountPaid - Amount already paid
 * @returns Percentage of payment completed (0-100)
 */
export const calculatePaymentPercentage = (
  totalAmount: number,
  amountPaid: number
): number => {
  if (totalAmount === 0) return 100
  const percentage = (amountPaid / totalAmount) * 100
  return Math.min(100, Math.max(0, percentage))
}
